import React, { useState, useEffect } from "react";
import LoadingSpinnerImage from "../Shared/LoadingSpinnerImage.js";

/*
 * Pulls a product's key fields from the Products Information module and drops a
 * formatted table into the section as a one-time paste -- it does not stay linked.
 */

// field key on the product DTO -> label shown in the picker / table
const FIELD_MAP = [
    ["SKUProductCode", "Product code"],
    ["ProductName", "Product name"],
    ["FamilyName", "Product family"],
    ["RiskClass", "Risk class"],
    // CountryOfOrigin/MadeIn are raw country IDs; these are the resolved names.
    ["CountryOf", "Country of origin"],
    ["MadeInCountry", "Made in"],
    ["BasicUDIDI", "Basic UDI-DI"],
    ["EMDNCode", "EMDN / GMDN"],
    ["IntendedPurpose", "Intended purpose"],
];

const firstOperatorName = (val) => {
    if (!val) return "";
    if (Array.isArray(val)) return val.map((o) => o?.OperatorName || o?.Establishment || o).filter(Boolean).join(", ");
    return String(val);
};

const InsertFromProductInfoPopup = ({ techDocId, onInsert, onClose }) => {
    const [search, setSearch] = useState("");
    const [products, setProducts] = useState([]);
    const [isLoadingList, setIsLoadingList] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [product, setProduct] = useState(null);
    const [isLoadingProduct, setIsLoadingProduct] = useState(false);
    const [checked, setChecked] = useState(() => FIELD_MAP.map(([k]) => k));

    useEffect(() => {
        const t = setTimeout(loadList, 250);
        return () => clearTimeout(t);
    }, [search]);

    const loadList = () => {
        setIsLoadingList(true);
        fetch(`/umbraco/api/RIMSProducts/Products/?page=1&search=${encodeURIComponent(search)}&sortBy=Name&sortType=ASCENDING`)
            .then((r) => r.json())
            .then((data) => {
                const list = Array.isArray(data) ? data : (data?.Items || data?.Results || []);
                setProducts(list.slice(0, 40));
            })
            .catch(() => setProducts([]))
            .finally(() => setIsLoadingList(false));
    };

    const pick = (id) => {
        setSelectedId(id);
        setIsLoadingProduct(true);
        setProduct(null);
        fetch(`/umbraco/api/RIMSProducts/Product/?id=${id}`)
            .then((r) => r.json())
            .then((data) => setProduct(data))
            .catch(() => setProduct(null))
            .finally(() => setIsLoadingProduct(false));
    };

    const rowsFor = (p) => {
        const rows = [];
        FIELD_MAP.forEach(([key, label]) => {
            if (!checked.includes(key)) return;
            let v = p?.[key];
            if (v == null || v === "") return;
            if (typeof v === "object") v = firstOperatorName(v);
            rows.push([label, String(v)]);
        });
        ["LegalManufacturers", "PhysicalManufacturers"].forEach((key) => {
            if (!checked.includes(key)) return;
            const v = firstOperatorName(p?.[key]);
            if (v) rows.push([key === "LegalManufacturers" ? "Legal manufacturer" : "Physical manufacturer", v]);
        });
        return rows;
    };

    const doInsert = () => {
        if (!product) return;
        const rows = rowsFor(product);
        const esc = (s) => String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
        const title = product.ProductName || product.SKUProductCode || "Product";
        const html =
            `<table style="width:100%;border-collapse:collapse;margin:12px 0;font-size:12.5px;">` +
            `<tbody>` +
            `<tr><th colspan="2" style="text-align:left;background:#f7fafc;border:1px solid #e2e8f0;padding:7px 10px;">${esc(title)}</th></tr>` +
            rows
                .map(
                    ([k, v]) =>
                        `<tr><td style="border:1px solid #e2e8f0;padding:7px 10px;width:34%;color:#4a5568;">${esc(k)}</td>` +
                        `<td style="border:1px solid #e2e8f0;padding:7px 10px;">${esc(v)}</td></tr>`
                )
                .join("") +
            `</tbody></table><p>Source: Products Information module &mdash; inserted ${new Date().toLocaleDateString("en-GB")}.</p>`;
        onInsert(html);
    };

    const toggle = (key) =>
        setChecked((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));

    return (
        <div className="td-modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
            <div className="td-modal wide">
                <div className="td-modal-h">
                    <h3>Insert from Product Info</h3>
                    <span className="td-close-x" onClick={onClose}><i className="fas fa-times" /></span>
                </div>
                <div className="td-modal-b">
                    <span className="td-lbl">Product</span>
                    <input
                        placeholder="Search products…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        style={{ marginBottom: 10 }}
                    />
                    <div style={{ maxHeight: 160, overflowY: "auto", border: "1px solid #e2e8f0", borderRadius: 6, marginBottom: 18 }}>
                        {isLoadingList ? (
                            <div className="td-center-loading" style={{ marginTop: 14 }}><LoadingSpinnerImage /></div>
                        ) : products.length === 0 ? (
                            <div className="td-muted" style={{ padding: 12, fontSize: 12 }}>No products found.</div>
                        ) : (
                            products.map((p) => (
                                <div
                                    key={p.Id}
                                    onClick={() => pick(p.Id)}
                                    style={{
                                        padding: "8px 12px",
                                        fontSize: 12.5,
                                        cursor: "pointer",
                                        background: selectedId === p.Id ? "#ebf8ff" : "transparent",
                                        borderBottom: "1px solid #f0f4f8",
                                    }}
                                >
                                    <b>{p.SKUProductCode}</b> &nbsp; {p.ProductName}
                                </div>
                            ))
                        )}
                    </div>

                    <span className="td-lbl">Fields to include</span>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4px 14px", marginBottom: 16 }}>
                        {[...FIELD_MAP, ["LegalManufacturers", "Legal manufacturer"], ["PhysicalManufacturers", "Physical manufacturer"]].map(([k, label]) => (
                            <label key={k} style={{ fontSize: 12, display: "flex", gap: 6, alignItems: "center" }}>
                                <input type="checkbox" checked={checked.includes(k)} onChange={() => toggle(k)} />
                                {label}
                            </label>
                        ))}
                    </div>

                    {isLoadingProduct && <LoadingSpinnerImage />}
                    {product && !isLoadingProduct && (
                        <div className="td-receipt">
                            {rowsFor(product).length} field(s) will be inserted as a table for{" "}
                            <b>{product.ProductName || product.SKUProductCode}</b>.
                        </div>
                    )}
                </div>
                <div className="td-modal-f">
                    <span className="caption">A one-time paste — the section will not stay linked to the product.</span>
                    <div style={{ display: "flex", gap: 10 }}>
                        <button className="td-btn td-btn-cancel" onClick={onClose}>Cancel</button>
                        <button className="td-btn td-btn-primary" onClick={doInsert} disabled={!product}>
                            Insert
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default InsertFromProductInfoPopup;
